import React, { useEffect, useState } from 'react';
import { Button, Link, CancelButton } from '../ui';
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid';
import { UploadFeedbackResponse } from '../../store/submission/types';
import { APIValidationError } from '../../store/api';
import { Result } from '../../store/api.d';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { connect } from 'react-redux';
import { OpenSnackBarDTO } from '../../store/error/types';
import errorSlice from '../../store/error/slice';
import { getHref } from '../../helpers/download';
import { useTheme } from '@material-ui/core';
import MyGridCore from '../MyGridCore/MyGridCore';
import { DEFAULT_FONT_SIZE } from '../MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import Success from '../Success/Success';
import Loading from '../Loading/Loading';

const FeedbackCell: React.FC<GridCellProps> = (props) => {
  const field = props.field || '';
  const value = props.dataItem[field];

  const { has_error } = props.dataItem;

  return <td style={{ backgroundColor: has_error ? 'rgb(243, 23, 0, 0.32)' : 'rgb(55, 180, 0,0.32)' }}> {value}</td>;
};

type UploadState = 'CHOOSE_FILE' | 'PREVIEW' | 'SUCCESS' | 'LOADING';

interface Props {
  doPreview?: (file: File) => Promise<Result<UploadFeedbackResponse, APIValidationError>>;
  doSave?: (file: File) => Promise<Result<UploadFeedbackResponse, APIValidationError>>;
  onSuccess?: () => void;
  openSnackBarAction: (data: OpenSnackBarDTO) => void;
  templateName: 'invitations' | 'keywords' | 'assignments-of-submission' | 'assignments-of-reviewer' | 'subset-bidding';
  templateParams?: { [key: string]: any };
  error?: string;
  onFeedbackHandler?: (data: UploadFeedbackResponse) => void;
  onCancelHandler?: () => void;
  displayOpen?: boolean; // If true, automatically triggers file picker
}

const FileUploadUpdater: React.FC<Props> = ({
  doPreview,
  doSave,
  onSuccess,
  openSnackBarAction,
  templateName,
  templateParams,
  error,
  onFeedbackHandler,
  onCancelHandler,
  displayOpen,
}) => {
  const { t, i18n } = useTranslation();
  const [uploadState, setUploadState] = useState<UploadState>('CHOOSE_FILE');
  const fileInputRef = React.createRef<HTMLInputElement>();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [feedback, setFeedback] = useState<UploadFeedbackResponse | undefined>(undefined);
  const theme = useTheme();

  useEffect(() => {
    if (displayOpen) {
      fileInputRef.current?.click();

      /* Adds listener to capture a cancel click in the file picker */
      if (onCancelHandler) {
        fileInputRef.current?.addEventListener('cancel', onCancelHandler);
        return () => {
          fileInputRef.current?.removeEventListener('cancel', onCancelHandler);
        };
      }
    }
  }, []);

  let content;
  switch (uploadState) {
    case 'CHOOSE_FILE': {
      content = (
        <div style={displayOpen ? { display: 'none' } : {}}>
          <div className="mb-6">
            <Link href={getHref(`/web/templates/${templateName}`, templateParams)} className="mb-4 text-sm block w-max">
              {t('Download template')}
            </Link>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              disabled={feedback !== undefined}
              onInput={async () => {
                if (fileInputRef.current?.files?.length && doPreview) {
                  const file = fileInputRef.current.files[0];
                  setUploadState('LOADING');
                  const result = await doPreview(file);
                  switch (result.type) {
                    case 'ok':
                      setFile(file);
                      setFeedback(result.value);
                      setUploadState('PREVIEW');
                      onFeedbackHandler?.(result.value);
                      break;
                    case 'validation-error':
                    case 'error':
                      setUploadState('CHOOSE_FILE');
                      onCancelHandler?.();
                      openSnackBarAction({
                        message: t('Import error. Make sure to use the correct template.'),
                        severity: 'error',
                      });
                      break;
                  }
                }
              }}
            />
          </div>
          {error && (
            <span className="ml-0.5 text-sm" style={{ color: theme.palette.error.main }}>
              {error}
            </span>
          )}
        </div>
      );
      break;
    }
    case 'PREVIEW': {
      let gridColumnProps: GridColumnProps[];
      if (feedback?.data.columns) {
        gridColumnProps = feedback.data.columns.map((col, index) => ({
          key: index,
          field: col,
          title: col,
        }));
        gridColumnProps.push({
          // @ts-ignore
          key: gridColumnProps.length,
          field: 'Feedback',
          title: t('Feedback'),
          cell: FeedbackCell,
        });
      } else {
        gridColumnProps = [];
      }

      let inputData: any[] = [];
      let disabled = true;
      if (feedback?.data.rows) {
        inputData = [];
        feedback.data.rows.forEach((row) => {
          const item: any = {};
          feedback.data.columns.forEach((col, index) => {
            item[col] = row.values[index];
          });
          item['Feedback'] = row.feedback;
          item['has_error'] = row.has_error;
          inputData.push(item);
        });
        disabled = feedback.data.rows.filter((row) => row.has_error).length === feedback.data.rows.length;
      }

      content = (
        <>
          <MyGridCore
            allGridData={inputData}
            gridSettings={{
              gridColumnProps: gridColumnProps,
              advancedFilter: false,
              DATA_ITEM_KEY: '',
              SELECTED_FIELD: '',
              LABELABLE_ITEM_KEY: '',
              fontSize: DEFAULT_FONT_SIZE,
              dataState: {},
            }}
            selectedState={{}}
            dateFormat={null}
            selectable={false}
            showActionsColumn={false}
            showFilters={true}
          />
          <div className="buttonWrapper text-center mt-4">
            {onCancelHandler && <CancelButton onClick={onCancelHandler}>{t('Cancel')}</CancelButton>}
            {doSave && (
              <Button
                onClick={async () => {
                  if (file) {
                    setUploadState('LOADING');
                    const result = await doSave(file);
                    switch (result.type) {
                      case 'ok':
                        setFile(undefined);
                        setUploadState('SUCCESS');
                        break;
                      case 'validation-error':
                        openSnackBarAction({ message: result.value.validation[0].message, severity: 'error' });
                        break;
                    }
                  }
                }}
                disabled={disabled}
              >
                {t('Save')}
              </Button>
            )}
          </div>
        </>
      );
      break;
    }
    case 'SUCCESS': {
      let title, message, buttonText;
      switch (templateName) {
        case 'assignments-of-submission':
        case 'assignments-of-reviewer':
          title = t('Successful import');
          message = t('Assignments have been imported successfully from the template file.');
          buttonText = t('View assignments');
          break;
        default:
          title = t('Successful import');
          message = t('Data have been imported successfully from the template file.');
          buttonText = t('Back');
          break;
      }
      content = (
        <div className="h-full flex flex-col justify-center">
          <Success title={title} message={message} buttonText={buttonText} handleClick={onSuccess} />
        </div>
      );
      break;
    }
    case 'LOADING':
      content = <Loading compact={true} />;
      break;
  }

  return <div>{content}</div>;
};

const mapStateToProps = (state: AppState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  openSnackBarAction: (data: OpenSnackBarDTO) => dispatch(errorSlice.actions['OPEN:SNACKBAR'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FileUploadUpdater);
